/**
 * NewsListing component.
 * @module components/News/NewsListing
 */

import React from 'react';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import moment from 'moment';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Container, Header, Grid, Card, Form } from 'semantic-ui-react';
import { BodyClass, hasBlocksData } from '@plone/volto/helpers';
import { UniversalLink, FormattedDate, Pagination, Image } from '@plone/volto/components';
import { searchContent, getVocabulary } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import { keys, isEmpty, map, filter } from 'lodash';
import { useDebounce } from "use-debounce";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { formatDate } from '../../helpers'
import { Sharing } from '../';
import defaultImage from '../Footer/img/logo-proursa-blanc.svg';


const messages = defineMessages({
	labelText: {
		id: 'By words',
		defaultMessage: 'By words',
	},
	placeholderText: {
		id: 'What are you looking for?',
		defaultMessage: 'What are you looking for?',
	},
	defaultOptionSubjects: {
		id: 'All subjects',
		defaultMessage: 'All subjects',
	},
	labelSubject: {
		id: 'By category',
		defaultMessage: 'By category',
	},
	placeholderAllSubjects: {
		id: 'All subjects',
		defaultMessage: 'All subjects',
	},
	labelMonth: {
		id: 'Month',
		defaultMessage: 'Month',
	},
	placeholderAllMonths: {
		id: 'All months',
		defaultMessage: 'All months',
	},
	labelYear: {
		id: 'Year',
		defaultMessage: 'Year',
	},
	placeholderAllYears: {
		id: 'All years',
		defaultMessage: 'All years',
	},
});

const NewsListing = (props) => {
	const { content } = props;
	const lang = content.language.token;
	const dispatch = useDispatch();
	const intl = useIntl();
	const news = useSelector((state) => state.search.subrequests.news?.items);
	const total = useSelector((state) => state.search.subrequests.news?.total);
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(12);
	const [search, setSearch] = useState(0);
	const [searchableTextInput, setSearchableText] = useState('');
	const [searchableText] = useDebounce(searchableTextInput, 500);
	const [month, setMonth] = useState('');
	const [year, setYear] = useState('');
	const [subject, setSubject] = useState('');
	const vocabularySubjects = 'plone.app.vocabularies.Keywords';

	const months = [
		{ value: '', label: intl.formatMessage(messages.placeholderAllMonths) },
		{ value: 1, label: 'Gener' },
		{ value: 2, label: 'Febrer' },
		{ value: 3, label: 'Març' },
		{ value: 4, label: 'Abril' },
		{ value: 5, label: 'Maig' },
		{ value: 6, label: 'Juny' },
		{ value: 7, label: 'Juliol' },
		{ value: 8, label: 'Agost' },
		{ value: 9, label: 'Setembre' },
		{ value: 10, label: 'Octubre' },
		{ value: 11, label: 'Novembre' },
		{ value: 12, label: 'Desembre' },
	];

	const years = [
		{ value: '', label: intl.formatMessage(messages.placeholderAllYears) },
		...Array.from({ length: 20 }, (_, index) => {
			const year = moment().year() - index;
			return { value: year, label: year };
		}),
	];

	const rawItemsSubjects = useSelector(
	  	(state) => state.vocabularies[vocabularySubjects]?.items || [],
	  	shallowEqual,
	);

	const itemsSubjects = useMemo(() => [
	  	{ value: '', label: intl.formatMessage(messages.defaultOptionSubjects) },
	  	...rawItemsSubjects,
	], [rawItemsSubjects, intl]);

	const getSubject = function (Subject) {
		if (!itemsSubjects) {
			return Subject
		}
		for (let item of itemsSubjects) {
			if (item.value === Subject) return item.label
		}
		return Subject
	}

	useEffect(() => {
		dispatch(getVocabulary({ vocabNameOrURL: vocabularySubjects, lang: content.language.token }));
	}, [dispatch]);

	useEffect(() => {
		let contentFilter = {
			portal_type: ['News Item'],
			b_size: pageSize,
			b_start: currentPage * pageSize,
			review_state: 'published',
			SearchableText: searchableText,
			sort_on: 'effective',
			sort_order: 'reverse',
			metadata_fields: ['effective', 'Subject', 'hasPreviewImage', 'featured']
		};
		contentFilter['effective.query'] = [moment().format('YYYY/MM/DD HH:mm')]
		contentFilter['effective.range'] = 'max'

		if (subject && subject !== '') {
			contentFilter.Subject = subject;
		}

		if (month && year) {
			const startOfMonth = moment(`${year}-${month}`, 'YYYY-MM').startOf('month');
			const endOfMonth = moment(`${year}-${month}`, 'YYYY-MM').endOf('month');
			contentFilter['effective.query'] = [
				startOfMonth.format('YYYY/MM/DD') + ' 00:00',
				endOfMonth.format('YYYY/MM/DD') + ' 23:59'
			];
			contentFilter['effective.range'] = 'min:max';
		}
		else if (year) {
			const startOfYear = moment(`${year}-01-01`, 'YYYY-MM-DD').startOf('year');
			const endOfYear = moment(`${year}-12-31`, 'YYYY-MM-DD').endOf('year');

			contentFilter['effective.query'] = [
				startOfYear.format('YYYY/MM/DD') + ' 00:00',
				endOfYear.format('YYYY/MM/DD') + ' 23:59'
			];
			contentFilter['effective.range'] = 'min:max';
		}

		dispatch(searchContent(flattenToAppURL(content['@id']), contentFilter, 'news'));
	}, [dispatch, currentPage, pageSize, searchableText, month, year, subject, search]);


	useEffect(() => {
		const searchForm = document.querySelector('.searchList');
		const searchFormPos = searchForm ? searchForm.offsetTop : 0;
		let scrollPos = window.scrollY;
		if (scrollPos > searchFormPos && searchForm) {
			window.scrollTo({ top: searchFormPos })
		}
	}, [currentPage]);

	const onChangePage = (event, activePage) => {
		event.preventDefault();
		setCurrentPage(activePage.value);
	};

	const onChangePageSize = (event) => {
		event.preventDefault();
		setPageSize(parseInt(event.target.text));
		setCurrentPage(0);
	};

	const onChangeSearchableText = (event, data) => {
		event.preventDefault();
		setSearchableText(data.value);
		setCurrentPage(0);
	};

	const onChangeSubject = (event, data) => {
		event.preventDefault();
		setSubject(data.value);
		setCurrentPage(0);
	};

	const onChangeMonth = (event, data) => {
		event.preventDefault();
		setMonth(data.value);
		setCurrentPage(0);
	};

	const onChangeYear = (event, data) => {
		event.preventDefault();
		setYear(data.value);
		setCurrentPage(0);
	};

	const onSubmit = (event) => {
		event.preventDefault();
		setSearch(search + 1);
		setCurrentPage(0);
	};


	return (
		<>
			<BodyClass className='newslisting' />

			<Container>
				{hasBlocksData(content) ? (
					<Grid>
						<Grid.Column width={12}>
							<RenderBlocks {...props} />
						</Grid.Column>
					</Grid>
				) : null}

				<Form className="searchList rounded-1 bg-white d-print-none p-3" onSubmit={onSubmit}>
					<Header as="h2" className='justify-content-center searchList__title' size='small' textAlign='center'>
						<FormattedMessage id="Filter" defaultMessage="Filter" />
					</Header>
					<Form.Group widths="equal" className="searchList__formGroup align-items-end">
						<Form.Input
							id="textInputId"
							fluid
							label={intl.formatMessage(messages.labelText)}
							focus
							name="searchableText"
							value={searchableTextInput}
							onChange={onChangeSearchableText}
							placeholder={intl.formatMessage(messages.placeholderText)}
							type="text"
						/>
						<Form.Select
							id="subjectSelectId"
							fluid
							label={intl.formatMessage(messages.labelSubject)}
							options={map(itemsSubjects, (item, index) => {
								return {
									key: index,
									value: item.value,
									text: item.label,
								};
							})}
							name="subject"
							value={subject}
							onChange={onChangeSubject}
							placeholder={intl.formatMessage(messages.placeholderAllSubjects)}
						/>
						<Form.Select
							id="monthSelectId"
							fluid
							label={intl.formatMessage(messages.labelMonth)}
							options={months.map((month, index) => {
								return {
									key: index,
									value: month.value,
									text: month.label,
								};
							})}
							name="month"
							value={month}
							onChange={onChangeMonth}
							placeholder={intl.formatMessage(messages.placeholderAllMonths)}
						/>
						<Form.Select
							id="yearSelectId"
							fluid
							label={intl.formatMessage(messages.labelYear)}
							options={years.map((year, index) => {
								return {
									key: index,
									value: year.value,
									text: year.label,
								};
							})}
							name="year"
							value={year}
							onChange={onChangeYear}
							placeholder={intl.formatMessage(messages.placeholderAllYears)}
						/>
					</Form.Group>
				</Form>

				{!isEmpty(news) ? (
					<div className="news newsListing pt-1">
						<Grid className='news newsListing mt-5'>
							<Grid.Row columns={12}>
								{news.map((item, index) => (
									<Grid.Column className="mb-4" key={item['@id']}
										mobile={12}
										tablet={index === 0 ? 12 : 6}
										computer={index === 0 ? 12 : 4}
										widescreen={index === 0 ? 12 : 4}
									>
										<Card fluid as="article" className={`newsItem position-relative ${item?.image_field ? ' newsItem-hasImage' : ' newsItem--noImage'} ${index === 0 ? ' newsItem--first' : ' newsItem--regular'} `} >
											<figure className='newsItem__figure position-relative m-0'>
												<div className='overflow-hidden rounded-1 newsItem__figure__wrapper'>
													{item.image_field ? (
														<Image
															className="newsItem__img m-0 image"
															item={item}
															loading='lazy'
															alt=""
															sizes={(index === 0 ? '(max-width: 767px) 100vw, 50vw' : '(max-width: 767px) 100vw, (max-width: 991px) 50vw, 33.33vw')}
														/>
													) : (
														<img
															src={defaultImage} 
															className="w-100 h-auto"
															width="212" height="44"
															alt=""
															/>
													)}
												</div>
												<span className="newsItem__readmore" aria-hidden="true"><FontAwesomeIcon icon={faChevronRight} /></span>
											</figure>
											<Card.Content  >
												<Card.Header className={"newsItem__title" + (index === 0 ? ' me-5' : ' ' )} as="h2">
													<UniversalLink
														className="newsItem__link newsItem__title stretched-link"
														href={flattenToAppURL(item['@id'])}>
														{item.title}
													</UniversalLink>
												</Card.Header>
												<Card.Description as="p" className="newsItem__desc">{item.description}</Card.Description>
											</Card.Content>
											<Card.Content extra className=" newsItem__meta">
												<FormattedDate date={item.effective} className="newsItem__date "
													format={{
														weekday: "long",
														day: "2-digit",
														month: "long",
													}}
												/>
												{item?.Subject ? (
													<ul className='newsItem__meta__list list-unstyled my-0'>
														{map(item.Subject.slice(0, 1), (item, index) => (
															<li key={item}>
																<span className="newsItem__subject" >{getSubject(item)}</span>
															</li>
														))}
													</ul>
												) : null}
											</Card.Content>
											<Sharing id={item['@id']} title={item.title} url={flattenToAppURL(item['@id'])} />
										</Card>
									</Grid.Column>
								))}
							</Grid.Row>
						</Grid>

						{total > pageSize && (
							<nav className="pagination">
								<Pagination
									current={currentPage}
									total={Math.ceil(total / pageSize)}
									onChangePage={onChangePage}
									onChangePageSize={onChangePageSize}
								/>
							</nav>
						)}

					</div>
				) : (
					<p><FormattedMessage id="No news found" defaultMessage="No news found" /></p>
				)}

			</Container>

		</>
	);
};


export default NewsListing;
